<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Modal from '../../../../components/Modal.vue';
import CannedMarkdownEditor from './CannedMarkdownEditor.vue';

export default {
  components: {
    NextButton,
    TagMultiSelectComboBox,
    Modal,
    CannedMarkdownEditor,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edinboxIds: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      selectedInboxIds: [],
      selectedInboxesTouched: false,
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      inboxes: 'inboxes/getInboxes',
    }),
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
    inboxOptions() {
      return (this.inboxes || []).map(inbox => ({
        value: Number(inbox.id),
        label: inbox.name,
      }));
    },
  },
  watch: {
    edshortCode: {
      immediate: true,
      handler(value) {
        this.shortCode = value || '';
      },
    },
    edcontent: {
      immediate: true,
      handler(value) {
        this.content = value || '';
      },
    },
    edinboxIds: {
      immediate: true,
      handler() {
        this.selectedInboxesTouched = false;
        this.syncSelectedInboxesFromProps(true);
      },
    },
    inboxOptions: {
      immediate: true,
      handler() {
        this.syncSelectedInboxesFromProps();
      },
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
  },
  methods: {
    syncCanonicalContent() {
      this.content = String(this.content || '').replace(/\r\n/g, '\n');
      this.v$.content.$touch();
    },
    normalizedInboxIds() {
      return this.selectedInboxIds.map(id => Number(id)).filter(Boolean);
    },
    normalizedEditedInboxIds() {
      return Array.isArray(this.edinboxIds)
        ? this.edinboxIds.map(id => Number(id)).filter(Boolean)
        : [];
    },
    syncSelectedInboxesFromProps(force = false) {
      if (!force && this.selectedInboxesTouched) return;
      if (!this.inboxOptions.length) return;

      const selectedIds = this.normalizedEditedInboxIds();
      const availableIds = new Set(this.inboxOptions.map(inbox => inbox.value));
      this.selectedInboxIds = selectedIds.filter(id => availableIds.has(id));
    },
    handleInboxSelectionChange(value) {
      this.selectedInboxesTouched = true;
      this.selectedInboxIds = Array.isArray(value) ? value : [];
    },
    setPageName({ name }) {
      this.content = name || '';
      this.syncCanonicalContent();
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.selectedInboxIds = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    editCannedResponse() {
      this.syncCanonicalContent();
      // Show loading on button
      this.editCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
          inbox_ids: this.normalizedInboxIds(),
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label>
            Inboxes
          </label>
          <TagMultiSelectComboBox
            v-model="selectedInboxIds"
            class="mb-2"
            :options="inboxOptions"
            placeholder="Global (all inboxes)"
            search-placeholder="Buscar inbox"
            empty-state="Nenhum inbox encontrado"
            @update:model-value="handleInboxSelectionChange"
          />
          <p class="text-xs text-n-slate-10 mb-2">
            Deixe vazio para Global (todos os inboxes).
          </p>
        </div>

        <div class="w-full canned-content-field">
          <div class="canned-content-header">
            <label :class="{ error: v$.content.$error }">
              {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
            </label>
          </div>
          <div class="editor-wrap">
            <CannedMarkdownEditor
              v-model="content"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @input="syncCanonicalContent"
              @blur="syncCanonicalContent"
            />
          </div>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
.canned-content-field {
  position: relative;
}

.canned-content-header {
  min-height: 2rem;
  display: flex;
  align-items: center;
  margin-bottom: 0.375rem;
}

.editor-wrap :deep(.canned-markdown-editor) {
  min-height: 12.5rem;
}
</style>
